import { useEffect, useState } from 'react';
import { create } from 'zustand';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  action?: { label: string; onClick: () => void };
  duration?: number;
}

interface ToastStore {
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
  },
  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience function to show a toast from anywhere */
export const toast = {
  success: (message: string) => useToastStore.getState().addToast({ type: 'success', message }),
  error: (message: string, action?: ToastMessage['action']) =>
    useToastStore.getState().addToast({ type: 'error', message, action, duration: 8000 }),
  warning: (message: string) => useToastStore.getState().addToast({ type: 'warning', message }),
  info: (message: string) => useToastStore.getState().addToast({ type: 'info', message }),
};

/**
 * Toast container — renders active toast notifications.
 * Place once in the app layout.
 */
export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div className="toast-container" role="region" aria-label="Notifiche" aria-live="polite">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t }: { toast: ToastMessage }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const duration = t.duration || 5000;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => removeToast(t.id), 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [t.id, t.duration, removeToast]);

  return (
    <div className={`toast toast-${t.type} ${exiting ? 'toast-exit' : ''}`} role="alert">
      <span className="toast-message">{t.message}</span>
      {t.action && (
        <button className="toast-action" onClick={t.action.onClick}>
          {t.action.label}
        </button>
      )}
      <button
        className="toast-close"
        onClick={() => removeToast(t.id)}
        aria-label="Chiudi notifica"
      >
        ×
      </button>
    </div>
  );
}
